/** One day of the Days card's outline (plan §5 "Outline rows"; DESIGN.md §5 Zero-Re-Layout): a
 *  48 px row — its span label "Day 3–4" (600), its title with its subtitle muted, a 48×32
 *  thumbnail of its image (downsized, the original on click), the first line of its description
 *  muted — on the records outline's row chrome (`OutlineRowShell`: the tree item, the drag
 *  handle's and the actions' rooms). The open day (one at a time) opens in place under its row:
 *  its fields as FieldSlots (`day:<id>` — title, subtitle, days, image, description and footer;
 *  in view mode the stored values, the rich texts rendered), on the field grid the edit mode
 *  keeps. Closed, a day mounts no field.
 *
 *  Everything edit mode adds keeps its room in view mode, so pressing E moves nothing: the drag
 *  handle and the actions (the shell: the row menu, or Restore on a removed day), the state
 *  mark (changed / added / changed on the server), and — on an itinerary its feed re-syncs —
 *  the title verdict's 16 px slot: in edit mode the sync icon when the feed's next sync would
 *  delete or rewrite the day by its title (amber when it would), the evidence in its tooltip
 *  and the row's name. Edit mode says each day's state in words on the row itself, open or
 *  closed (§5): "Added" for a new day, "was “Bangkok arrival”" for a retitled one, "changed on
 *  the server" for a kept day another editor changed meanwhile — muted, after its title, inside
 *  the title's column (both ellipsize; the words take at most 60 % of it). A row subscribes to
 *  its own record only: a keystroke in another day never renders it. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Collapse, Tooltip, useTheme } from '@mui/material';
import { memo, type MouseEvent, type ReactNode } from 'react';

import type { ItineraryDayRecordFieldKey } from '@/api/itineraryRecord';
import type { RecordVerdict } from '@/features/records/editor/types';
import { FieldCellGrid, type FieldCell } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { StateMarks, stateWords } from '@/features/records/fields/RowMarks';
import { OutlineRowShell, type OutlineRowProps } from '@/features/records/outline/OutlineRowShell';
import { useOutlineNode } from '@/features/records/outline/store';
import { prefersReducedMotion } from '@/features/records/page/recordPage';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { uploadedImage } from '@/features/records/fields/widgets/uploadedImages';
import { IMAGE_PRESET, imageUrl, type ImageChunks } from '@/utils/imageUrl';

import { storedImage } from '../cards/displays';
import { ITINERARY_DAY_ENTITY, ITINERARY_DAY_REGISTRY } from '../registry';

import { dayLabel, dayRec, dayRowName, descriptionExcerpt, type DayRowModel } from './daysModel';

/** A day row's height — the 48 px the Days card's slot reserved per day, in both modes. */
export const DAY_ROW_PX = 48;
/** The actions' room (the row menu in edit mode), kept in view mode. */
export const DAY_ACTIONS_PX = 28;

/** The open day's fields, in `ItineraryDayRecordFieldKey` order; the rich texts take a row. */
const DAY_KEYS = (Object.keys(ITINERARY_DAY_REGISTRY) as ItineraryDayRecordFieldKey[]).sort(
  (a, b) => ITINERARY_DAY_REGISTRY[a].order - ITINERARY_DAY_REGISTRY[b].order,
);
const WIDE: ReadonlySet<ItineraryDayRecordFieldKey> = new Set(['description', 'footer']);

const textOf = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** "was “Bangkok arrival”" — a retitled day's title before the change; "was untitled". */
const wasTitle = (from: unknown): string => (textOf(from) ? `was “${textOf(from)}”` : 'was untitled');

/** A stored image's CDN keys (the detail's baselines hold them). */
function chunksOf(v: unknown): ImageChunks | null {
  if (!v || typeof v !== 'object') return null;
  const { p, s } = v as Partial<ImageChunks>;
  return typeof p === 'string' && typeof s === 'string' ? { p, s } : null;
}

/** A day's image as CDN keys, whatever its value's form: the keys themselves (the detail's
 *  baselines), the stored Image id (edit_context's — drawn from the detail's keys,
 *  `storedChunks`), or a picked upload's id (this tab's upload, `uploadedImage`). */
export function dayImageChunks(value: unknown, storedValue: unknown, storedChunks: ImageChunks | null): ImageChunks | null {
  const own = chunksOf(value);
  if (own) return own;
  if (typeof value !== 'number') return null;
  if (value === storedValue) return storedChunks;
  const up = uploadedImage(value)?.image;
  return up?.p && up.s ? { p: up.p, s: up.s } : null;
}

/** The row's columns: the span label (as wide as the longest, `--day-label-w`), the title and
 *  subtitle, the thumbnail, the description's first line — the line and then the thumbnail
 *  give way on a narrow card (the outline is the `days` container). */
const ROW_GRID_SX = {
  flex: '1 1 auto',
  minWidth: 0,
  display: 'grid',
  alignItems: 'center',
  columnGap: 1.5,
  typography: 'body2',
  gridTemplateColumns: 'var(--day-label-w, 7ch) minmax(0, 1fr) 48px minmax(0, 1.25fr)',
  '@container days (max-width: 640px)': {
    gridTemplateColumns: 'var(--day-label-w, 7ch) minmax(0, 1fr) 48px',
    '& [data-day-excerpt]': { display: 'none' },
  },
  '@container days (max-width: 400px)': {
    gridTemplateColumns: 'var(--day-label-w, 7ch) minmax(0, 1fr)',
    '& [data-day-thumb]': { display: 'none' },
  },
} as const;

const ELLIPSIS_SX = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** The day's state in words, after its title: muted caption, giving way after the title. */
const STATE_WORDS_SX = { ...ELLIPSIS_SX, flex: '0 1 auto', maxWidth: '60%', typography: 'caption', color: 'text.secondary' } as const;

const THUMB_SX = {
  width: 48,
  height: 32,
  objectFit: 'cover',
  borderRadius: '4px',
  border: 1,
  borderColor: 'divider',
  display: 'block',
} as const;

/** The image, downsized, linking the original — out of the tab order: the outline is one tab
 *  stop, and the open day's Image field is the keyboard's way to it. */
function DayThumb({ image, name }: { image: ImageChunks | null; name: string }) {
  const thumb = image ? imageUrl(image, IMAGE_PRESET.THUMB) : null;
  if (!thumb) return <Box data-day-thumb sx={{ width: 48, height: 32 }} />;
  return (
    <Box
      component="a"
      data-day-thumb
      href={imageUrl(image) ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      tabIndex={-1}
      aria-label={`Open the image of ${name} in a new tab`}
      onClick={(e: MouseEvent) => e.stopPropagation()}
      sx={{ display: 'block', width: 48, height: 32 }}
    >
      <Box component="img" src={thumb} alt="" loading="lazy" sx={THUMB_SX} />
    </Box>
  );
}

/** The open day's fields: FieldSlots of `day:<id>` on the field grid (its columns pinned by the
 *  card, `FIELD_GRID_VAR`), the image downsized. */
function DayFields({ id, name, image }: { id: number; name: string; image: ImageChunks | null }) {
  const rec = dayRec(id);
  const cells: FieldCell[] = DAY_KEYS.map((key) => ({
    key: `${rec}:${key}`,
    rec,
    path: key,
    wide: WIDE.has(key),
    node: (
      <FieldSlot
        fieldKey={key}
        entity={ITINERARY_DAY_ENTITY}
        id={id}
        path={key}
        inlineChoices={false}
        {...(key === 'image' ? { display: storedImage(image, 'Image') } : {})}
      />
    ),
  }));
  return (
    <Box role="group" aria-label={`Fields of ${name}`} data-day-open={id} sx={{ pt: 1, pb: 2 }}>
      <FieldCellGrid sx={FIELD_GRID_SX} cells={cells} />
    </Box>
  );
}

export interface DayRowProps extends OutlineRowProps {
  row: DayRowModel;
  /** Its fields are open under it (one day at a time). */
  open: boolean;
  /** Edit mode: the row reads the draft. */
  editing: boolean;
  /** Edit mode and allowed: the handle and the actions show. */
  live: boolean;
  /** The itinerary's feed re-syncs its days by title: the title verdict's room (edit mode
   *  shows it) is kept. */
  verdictRoom: boolean;
  /** The day's stored image as the detail has it (its CDN keys). */
  storedImage: ImageChunks | null;
  /** Edit mode's row actions (the menu, or Restore); null in view mode. */
  actions?: ReactNode;
  /** A day's title verdict, given whether the draft retitled it (edit mode). */
  dayVerdict?: (dayId: number, retitled: boolean) => RecordVerdict | null;
}

export const DayRow = memo(function DayRow({
  row,
  index,
  active,
  selected,
  onSelect,
  open,
  editing,
  live,
  verdictRoom,
  storedImage,
  actions = null,
  dayVerdict,
}: DayRowProps) {
  const node = useOutlineNode(dayRec(row.id));
  const title = textOf(node.value('title'));
  const subtitle = textOf(node.value('subtitle'));
  const image = dayImageChunks(node.value('image'), node.stored('image'), storedImage);
  const excerpt = descriptionExcerpt(node.value('description'));
  const changed = editing && node.entries !== undefined;
  const kept = editing && !row.removed;
  // A day the draft adds: created on save.
  const added = kept && row.id < 0;
  // A saved day retitled: what it was called when the draft took it (the slot's "was …").
  const retitle = kept && row.id > 0 ? node.entry('title') : undefined;
  const was = retitle ? wasTitle(retitle.from) : null;
  // A kept day another editor changed meanwhile (Keep mine / Take theirs in its fields).
  const stale = kept && Object.values(node.entries ?? {}).some((e) => e.stale && !e.stale.gone);
  const words = [added ? 'Added' : null, was, stale ? 'changed on the server' : null].filter(Boolean).join(' · ');
  const name = dayRowName({ range: row.range, title });
  // The feed's word on this day, by its title (edit mode; a removed day is going anyway).
  const verdict = editing && verdictRoom && !row.removed ? (dayVerdict?.(row.id, node.entry('title') !== undefined) ?? null) : null;
  const feedNote = verdict && verdict.verdict !== 'kept' ? verdict : null;
  const label =
    [name, subtitle, open ? 'open' : null].filter(Boolean).join(' · ') +
    stateWords({ changed, invalid: null, removed: row.removed, added, stale, was }) +
    (feedNote ? ` · ${feedNote.evidence}` : '');
  const reduced = prefersReducedMotion();
  // The standard Collapse (the surface's motion grammar): the theme's standard duration, never
  // one that grows with the day's height; none under reduced motion.
  const { transitions } = useTheme();

  return (
    <Box
      data-day-row={row.id}
      sx={{
        position: 'relative',
        // A hairline between days, drawn over the row so the row stays 48 px.
        ...(index > 0 && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            bgcolor: 'divider',
            zIndex: 1,
            pointerEvents: 'none',
          },
        }),
      }}
    >
      <OutlineRowShell
        index={index}
        row={row}
        active={active}
        selected={selected}
        onSelect={onSelect}
        live={live}
        level={1}
        name={name}
        label={label}
        height={DAY_ROW_PX}
        changed={changed || added}
        actionsWidth={DAY_ACTIONS_PX}
        actions={actions}
      >
        <Box sx={ROW_GRID_SX}>
          <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} data-day-label>
            {dayLabel(row.range)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <Box sx={{ ...ELLIPSIS_SX, flex: '0 1 auto' }} title={[title, subtitle].filter(Boolean).join(' · ') || undefined}>
              {title || (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Untitled
                </Box>
              )}
              {subtitle && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' · '}
                  {subtitle}
                </Box>
              )}
            </Box>
            {words && (
              <Box component="span" data-day-state sx={STATE_WORDS_SX} title={words}>
                {words}
              </Box>
            )}
          </Box>
          <DayThumb image={image} name={name} />
          <Box sx={{ ...ELLIPSIS_SX, color: 'text.secondary' }} data-day-excerpt title={excerpt || undefined}>
            {excerpt}
          </Box>
        </Box>
        <StateMarks changed={changed} invalid={null} added={added} stale={stale} />
        {verdictRoom && (
          <Box aria-hidden data-day-verdict sx={{ width: 16, height: 16, flex: 'none', display: 'inline-flex' }}>
            {feedNote && (
              // The words are in the row's name; the icon's tooltip shows them on hover.
              <Tooltip title={feedNote.evidence}>
                <SyncIcon
                  data-verdict={feedNote.verdict}
                  sx={{ fontSize: 16, color: feedNote.verdict === 'overwrites' ? 'warning.main' : 'text.secondary' }}
                />
              </Tooltip>
            )}
          </Box>
        )}
        <ExpandMoreIcon
          aria-hidden
          sx={{
            fontSize: 20,
            flex: 'none',
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: reduced ? 'none' : 'transform 150ms ease-out',
          }}
        />
      </OutlineRowShell>
      <Collapse in={open} timeout={reduced ? 0 : transitions.duration.standard} unmountOnExit data-day-collapse>
        <DayFields id={row.id} name={name} image={image} />
      </Collapse>
    </Box>
  );
});
